import { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Link } from 'react-router-dom';
import { Mark } from '../components/Layout';
import { LanguageSwitch } from '../components/ui';
import { isNativeApp } from '../lib/app';
import { useTenant } from '../lib/tenant';

/** Chrome/Edge/Android offer this event when the app can be installed with one tap */
interface InstallPrompt extends Event {
  prompt: () => Promise<void>;
}

/** The school's own front page: its logo, name and message, how pickup works for parents, and how to install the app */
export function SchoolWelcome() {
  const { t, i18n } = useTranslation();
  const tenant = useTenant();
  const school = tenant.school!;
  const ar = i18n.language === 'ar';
  const name = (ar && school.nameAr) || school.name;
  const message = (ar ? school.welcomeMessageAr || school.welcomeMessage : school.welcomeMessage || school.welcomeMessageAr) || t('welcome.defaultMessage', { school: name });
  const [install, setInstall] = useState<InstallPrompt | null>(null);

  useEffect(() => {
    document.title = name;
    const onPrompt = (e: Event) => {
      e.preventDefault();
      setInstall(e as InstallPrompt);
    };
    window.addEventListener('beforeinstallprompt', onPrompt);
    return () => window.removeEventListener('beforeinstallprompt', onPrompt);
  }, [name]);

  return (
    <div className="sw">
      <header className="sw-top">
        <div className="sw-row">
          <span className="sw-brand">
            {school.logoUrl ? <img src={school.logoUrl} alt="" /> : <Mark />}
            <span>{name}</span>
          </span>
          <LanguageSwitch />
          <Link to="/login" className="sw-signin">
            {t('auth.signIn')}
          </Link>
        </div>
      </header>

      <main>
        <section className="sw-hero">
          <div className="sw-row sw-hero-inner">
            {school.logoUrl && <img className="sw-hero-logo" src={school.logoUrl} alt="" />}
            <h1>{t('welcome.title', { school: name })}</h1>
            <p className="sw-message">{message}</p>
            <div className="sw-cta">
              <Link to="/login" className="btn btn-primary sw-btn">
                {t('auth.signIn')}
              </Link>
              <a href="#install" className="btn sw-btn">
                {t('welcome.installCta')}
              </a>
            </div>
          </div>
        </section>

        <section className="sw-section">
          <div className="sw-row">
            <h2>{t('welcome.howTitle')}</h2>
            <ol className="sw-steps">
              <li>{t('welcome.step1')}</li>
              <li>{t('welcome.step2')}</li>
              <li>{t('welcome.step3')}</li>
            </ol>
            <p className="sw-note">{t('welcome.walkers')}</p>
          </div>
        </section>

        <section className="sw-section" id="install">
          <div className="sw-row">
            <h2>{t('welcome.installTitle')}</h2>
            <p className="muted">{t('welcome.installLede')}</p>
            {install && (
              <button type="button" className="btn btn-primary" onClick={() => void install.prompt().then(() => setInstall(null))}>
                {t('welcome.installNow')}
              </button>
            )}
            <dl className="sw-install">
              <div>
                <dt>iPhone</dt>
                <dd>{t('welcome.iphone')}</dd>
              </div>
              <div>
                <dt>Android</dt>
                <dd>{t('welcome.android')}</dd>
              </div>
            </dl>
          </div>
        </section>
      </main>

      <footer className="sw-footer">
        <div className="sw-row">
          {isNativeApp() ? <span>{t('welcome.poweredBy', { name: tenant.platformName })}</span> : <a href={tenant.platformUrl}>{t('welcome.poweredBy', { name: tenant.platformName })}</a>}
        </div>
      </footer>
    </div>
  );
}
